/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { useEffect, useState } from 'react'

import { Moment } from 'config/moment'
import { useApiCall } from 'hooks/useApi'
import { ConfigUtils } from 'utils/config'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { MetricCard } from '../../components/MetricCard'
import {
  getGeneralMetrics,
  GeneralMetricsData,
  GeneralMetricsParams,
  GeneralMetricsResponse,
} from './api'

export type GeneralMetricsDisplayData = {
  totalRegistros: { value: number; prev: number; diff: number; isPositive: boolean }
  usuariosUnicos: { value: number; prev: number; diff: number; isPositive: boolean }
  frecuenciaPromedio: { value: number; prev: number; diff: number; isPositive: boolean }
  registrosFisicos: { value: number; prev: number; diff: number; isPositive: boolean }
  registrosDelivery: { value: number; prev: number; diff: number; isPositive: boolean }
}

const calculateDiff = (current: number, previous: number) => {
  if (previous === 0) return 0
  return ((current - previous) / previous) * 100
}

const formatStats = (
  current?: GeneralMetricsData | null,
  previous?: GeneralMetricsData | null,
): GeneralMetricsDisplayData => {
  if (!previous) {
    return {
      totalRegistros: {
        value: current?.totalRecords || 0,
        prev: 0,
        diff: 0,
        isPositive: true,
      },
      usuariosUnicos: {
        value: current?.uniqueUsers || 0,
        prev: 0,
        diff: 0,
        isPositive: true,
      },
      frecuenciaPromedio: {
        value: current?.averageFrequency || 0,
        prev: 0,
        diff: 0,
        isPositive: true,
      },
      registrosFisicos: {
        value: current?.physicalRecords || 0,
        prev: 0,
        diff: 0,
        isPositive: true,
      },
      registrosDelivery: {
        value: current?.deliveryRecords || 0,
        prev: 0,
        diff: 0,
        isPositive: true,
      },
    }
  }

  return {
    totalRegistros: {
      value: current?.totalRecords || 0,
      prev: previous.totalRecords || 0,
      diff: calculateDiff(current?.totalRecords || 0, previous?.totalRecords || 0),
      isPositive: (current?.totalRecords || 0) >= (previous?.totalRecords || 0),
    },
    usuariosUnicos: {
      value: current?.uniqueUsers || 0,
      prev: previous.uniqueUsers || 0,
      diff: calculateDiff(current?.uniqueUsers || 0, previous?.uniqueUsers || 0),
      isPositive: (current?.uniqueUsers || 0) >= (previous?.uniqueUsers || 0),
    },
    frecuenciaPromedio: {
      value: current?.averageFrequency || 0,
      prev: previous.averageFrequency || 0,
      diff: calculateDiff(current?.averageFrequency || 0, previous?.averageFrequency || 0),
      isPositive: (current?.averageFrequency || 0) >= (previous?.averageFrequency || 0),
    },
    registrosFisicos: {
      value: current?.physicalRecords || 0,
      prev: previous.physicalRecords || 0,
      diff: calculateDiff(current?.physicalRecords || 0, previous?.physicalRecords || 0),
      isPositive: (current?.physicalRecords || 0) >= (previous?.physicalRecords || 0),
    },
    registrosDelivery: {
      value: current?.deliveryRecords || 0,
      prev: previous.deliveryRecords || 0,
      diff: calculateDiff(current?.deliveryRecords || 0, previous?.deliveryRecords || 0),
      isPositive: (current?.deliveryRecords || 0) >= (previous?.deliveryRecords || 0),
    },
  }
}

interface GeneralMetricsProps {
  period1: { startDate: Moment; endDate: Moment }
  period2: { startDate: Moment; endDate: Moment }
  compara: boolean
  trigger: number
  storeIds?: number[]
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
  // eslint-disable-next-line react/require-default-props
  onSnapshot?: (snapshot: { isLoading: boolean; displayData: GeneralMetricsDisplayData }) => void
  // eslint-disable-next-line react/require-default-props
  hidePhysicalAndDelivery?: boolean
}

const GeneralMetrics = ({
  period1,
  period2,
  compara,
  trigger,
  storeIds,
  onLoaded,
  onSnapshot,
  hidePhysicalAndDelivery,
}: GeneralMetricsProps) => {
  const [data, setData] = useState<GeneralMetricsData | null>(null)
  const [data2, setData2] = useState<GeneralMetricsData | null>(null)
  const [metricsCall, isLoading] = useApiCall<GeneralMetricsParams, GeneralMetricsResponse>(
    getGeneralMetrics,
  )

  // Track loading state
  useLoadingTracker({ isLoading, componentId: 'GeneralMetrics' })

  const fetchData = async () => {
    try {
      const startDate1 = period1.startDate.format('YYYY-MM-DD')
      const endDate1 = period1.endDate.format('YYYY-MM-DD')

      const response1 = await metricsCall({
        startDate: startDate1,
        endDate: endDate1,
        ...(storeIds && storeIds.length > 0 && { storeIds: storeIds.join(',') }),
      })

      if (response1?.data) {
        setData(response1.data)
      }

      if (compara) {
        const startDate2 = period2.startDate.format('YYYY-MM-DD')
        const endDate2 = period2.endDate.format('YYYY-MM-DD')

        const response2 = await metricsCall({
          startDate: startDate2,
          endDate: endDate2,
          ...(storeIds && storeIds.length > 0 && { storeIds: storeIds.join(',') }),
        })

        if (response2?.data) {
          setData2(response2.data)
        }
      } else {
        setData2(null)
      }
    } catch (error) {
      console.error('Error fetching general metrics:', error)
      setData(null)
      setData2(null)
    } finally {
      if (onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  const displayData = React.useMemo(() => formatStats(data, data2 || undefined), [data, data2])

  useEffect(() => {
    if (!onSnapshot) return
    onSnapshot({ isLoading, displayData })
  }, [onSnapshot, isLoading, displayData])

  const hasDeliveryIntegration = ConfigUtils.integrations.tucan.enabled

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard
          title="Total de registros"
          value={displayData.totalRegistros.value}
          prev={displayData.totalRegistros.prev}
          diff={displayData.totalRegistros.diff}
          isPositive={displayData.totalRegistros.isPositive}
          showComparison={compara}
          comparisonMonth={period2.startDate.format('DD/MM')}
          comparisonYear={period2.endDate.format('DD/MM/YYYY')}
          isLoading={isLoading}
        />
        <MetricCard
          title="Usuarios únicos"
          value={displayData.usuariosUnicos.value}
          prev={displayData.usuariosUnicos.prev}
          diff={displayData.usuariosUnicos.diff}
          isPositive={displayData.usuariosUnicos.isPositive}
          showComparison={compara}
          comparisonMonth={period2.startDate.format('DD/MM')}
          comparisonYear={period2.endDate.format('DD/MM/YYYY')}
          isLoading={isLoading}
        />
        <MetricCard
          title="Frecuencia promedio"
          value={displayData.frecuenciaPromedio.value}
          prev={displayData.frecuenciaPromedio.prev}
          diff={displayData.frecuenciaPromedio.diff}
          isPositive={displayData.frecuenciaPromedio.isPositive}
          showComparison={compara}
          comparisonMonth={period2.startDate.format('DD/MM')}
          comparisonYear={period2.endDate.format('DD/MM/YYYY')}
          isLoading={isLoading}
        />
      </div>
      {!hidePhysicalAndDelivery && hasDeliveryIntegration && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <MetricCard
            title="Registros físicos"
            value={displayData.registrosFisicos.value}
            prev={displayData.registrosFisicos.prev}
            diff={displayData.registrosFisicos.diff}
            isPositive={displayData.registrosFisicos.isPositive}
            showComparison={compara}
            comparisonMonth={period2.startDate.format('DD/MM')}
            comparisonYear={period2.endDate.format('DD/MM/YYYY')}
            isLoading={isLoading}
          />
          <MetricCard
            title="Registros delivery"
            value={displayData.registrosDelivery.value}
            prev={displayData.registrosDelivery.prev}
            diff={displayData.registrosDelivery.diff}
            isPositive={displayData.registrosDelivery.isPositive}
            showComparison={compara}
            comparisonMonth={period2.startDate.format('DD/MM')}
            comparisonYear={period2.endDate.format('DD/MM/YYYY')}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  )
}

GeneralMetrics.defaultProps = {
  storeIds: undefined,
}

export default GeneralMetrics
